import { useViewer } from "@/authentication/ViewerProvider"
import Container from "@/components/ui/container"
import ErrorScreen from "@/components/ui/error-screen"
import { H1 } from "@/components/ui/headings"
import Panel from "@/components/ui/panel"
import { gql } from "@/generated"
import LoadingScreen from "@/novus/components/loading-screen"
import { MainLayout } from "@/novus/layouts/MainLayout"
import { useQuery } from "@apollo/client"
import { ExternalLink } from "lucide-react"
import React from "react"
import { Deductions } from "./Deductions"
import FiveTwoNineAccountsList from "./FiveTwoNineAccountsList"
import MemberProfilePanel from "./MemberProfilePanel"

const UserProfileQuery = gql(`
  query UserProfile($userId: ID!, $deductionsAfter: String, $fiveTwoNineAccountsAfter: String) {
    ...ProfileFiveTwoNineAccounts_QueryFragment
    ...ProfileDeductions_QueryFragment
  }
`)

const UserProfile = () => {
  const { viewer } = useViewer()
  const { data, error, loading, fetchMore } = useQuery(UserProfileQuery, {
    variables: {
      userId: viewer.id,
    },
  })

  if (loading && !data) return <LoadingScreen />
  if (error || !data) return <ErrorScreen message={error?.message} />

  return (
    <MainLayout>
      <Container>
        <div className="flex flex-col gap-5">
          <MemberProfilePanel
            fiveTwoNineAccountsCount={data.fiveTwoNineAccounts.totalCount}
            deductionsCount={data.deductions.totalCount}
          />

          <div>
            <H1>Beneficiaries</H1>
            <Panel>
              {data.fiveTwoNineAccounts && (
                <FiveTwoNineAccountsList
                  data={data.fiveTwoNineAccounts}
                  loading={loading}
                  fetchMore={(after: string) => {
                    fetchMore({
                      variables: {
                        fiveTwoNineAccountsAfter: after,
                      },
                    })
                  }}
                />
              )}
            </Panel>
          </div>

          <div>
            <H1>Monthly Deductions</H1>
            <Panel>
              <Deductions
                deductions={data.deductions}
                loading={loading}
                fetchMore={(after: string) => {
                  fetchMore({
                    variables: {
                      deductionsAfter: after,
                    },
                  })
                }}
                requiresFiveTwoNineAccounts={
                  data.fiveTwoNineAccounts.totalCount <= 0
                }
              />
            </Panel>
          </div>

          <div>
            <h1 className="text-xl mb-0 md:text-5xl text-black font-semibold">
              <a
                href="/#download"
                target="_blank"
                rel="noopener noreferrer"
                className="underline text-black hover:text-gray-700 flex items-center gap-2 hover:underline"
              >
                Download the <span className="text-primary">Hadley App</span>
                <ExternalLink className="w-5 h-5" />
              </a>
            </h1>
            <Panel>
              <div className="space-y-4 text-lg -mt-2">
                <p>
                  Link your 529 accounts and add family and friends to save
                  more, together.
                </p>
                <p>
                  Set up weekly, monthly, and even annual recurring
                  contributions to your accounts – and for others.
                </p>
                <p>
                  Say hello to your new favorite birthday, holiday, tooth fairy,
                  just-because gift. Saving just got way more social.
                </p>
              </div>
            </Panel>
          </div>
        </div>
      </Container>
    </MainLayout>
  )
}

export default UserProfile
